(() => {
  const vimeoPosterCache = new Map();

  const fetchVimeoPoster = (vimeoId) => {
    if (!vimeoPosterCache.has(vimeoId)) {
      const promise = fetch(
        `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
          `https://vimeo.com/${vimeoId}`,
        )}`,
      )
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => (data && data.thumbnail_url) || null)
        .catch(() => null);
      vimeoPosterCache.set(vimeoId, promise);
    }
    return vimeoPosterCache.get(vimeoId);
  };

  const init = (section) => {
    const swiperEl = section.querySelector(".video-reel__swiper");
    if (!swiperEl || swiperEl._vrInit) return;
    swiperEl._vrInit = true;

    const prevBtn = section.querySelector(".video-reel__btn-prev");
    const nextBtn = section.querySelector(".video-reel__btn-next");
    const paginationEl = section.querySelector(".video-reel__pagination");
    const delayMs = Number(section.dataset.autoplayDelay) || 0;
    const slideCount = parseInt(section.dataset.slideCount, 10) || 5;

    /* ---- Per-slide mute state ---- */
    // slideMuted[realIndex] = false → unmuted; missing key → muted (default)
    const slideMuted = {};

    /* ---- Video type helpers ---- */

    const getVideoType = (slide) => {
      const container = slide.querySelector(".video-reel__video-container");
      return container?.dataset.videoType || "native";
    };

    const getIframe = (slide) => slide.querySelector(".video-reel__iframe");

    const postYT = (iframe, func, args = []) => {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func, args }),
        "*",
      );
    };

    const postVimeo = (iframe, method, value) => {
      const msg = { method };
      if (value !== undefined) msg.value = value;
      iframe.contentWindow?.postMessage(
        JSON.stringify(msg),
        "https://player.vimeo.com",
      );
    };

    const getSlideRealIndex = (slide) => {
      const idx = parseInt(slide.getAttribute("data-swiper-slide-index"), 10);
      return isNaN(idx) ? -1 : idx;
    };

    const applySlideAudio = (slide, muted) => {
      const type = getVideoType(slide);
      const unmutedIcon = slide.querySelector(".video-reel__icon-unmuted");
      const mutedIcon = slide.querySelector(".video-reel__icon-muted");
      if (type === "youtube") {
        const iframe = getIframe(slide);
        if (iframe) postYT(iframe, muted ? "mute" : "unMute");
      } else if (type === "vimeo") {
        const iframe = getIframe(slide);
        if (iframe) postVimeo(iframe, "setMuted", muted);
      } else {
        const video = slide.querySelector(".video-reel__video");
        if (video) video.muted = muted;
      }
      if (unmutedIcon) unmutedIcon.style.display = muted ? "none" : "block";
      if (mutedIcon) mutedIcon.style.display = muted ? "block" : "none";
    };

    // Toggle mute for one real-index — syncs originals + all loop clones
    const toggleSlideAudio = (swiper, fromSlide) => {
      const realIdx = getSlideRealIndex(fromSlide);
      const currentMuted = slideMuted[realIdx] !== false; // default: true
      const newMuted = !currentMuted;
      slideMuted[realIdx] = newMuted;
      swiper.slides.forEach((s) => {
        if (getSlideRealIndex(s) === realIdx) applySlideAudio(s, newMuted);
      });
    };

    /* ---- Helpers ---- */

    const getBullets = () =>
      Array.from(section.querySelectorAll(".video-reel__bullet"));

    const clearProgress = () => {
      getBullets().forEach((b) => {
        b.classList.remove("is-progress-active");
        b.style.removeProperty("--vr-progress-duration");
        // Force animation reset for next activation
        const ring = b.querySelector(".svg__progress-circle");
        if (ring) {
          ring.style.animation = "none";
          ring.offsetWidth; // reflow
          ring.style.animation = "";
        }
      });
    };

    const setProgress = (swiper) => {
      clearProgress();
      if (!swiper.autoplay?.running || delayMs <= 0) return;

      // Use activeIndex → data-swiper-slide-index, same as playActiveVideos.
      // swiper.realIndex drifts and gets stuck during loop-fix silent
      // transitions, which froze the progress ring on one bullet.
      const activeSlide = swiper.slides[swiper.activeIndex];
      const realIndex = activeSlide ? getSlideRealIndex(activeSlide) : 0;
      const bullets = getBullets();
      const activeBullet = bullets[realIndex];
      if (!activeBullet) return;

      activeBullet.style.setProperty("--vr-progress-duration", `${delayMs}ms`);
      activeBullet.offsetWidth; // reflow to guarantee animation restarts
      activeBullet.classList.add("is-progress-active");
    };

    // Use activeIndex (numeric) — accurate even during loop-fix silent transitions
    const playActiveVideos = (swiper) => {
      const activeIdx = swiper.activeIndex;
      Array.from(swiper.slides).forEach((slide, idx) => {
        const type = getVideoType(slide);
        if (type === "youtube") {
          const iframe = getIframe(slide);
          if (!iframe) return;
          if (idx === activeIdx) {
            postYT(iframe, "playVideo");
          } else {
            postYT(iframe, "pauseVideo");
          }
        } else if (type === "vimeo") {
          const iframe = getIframe(slide);
          if (!iframe) return;
          if (idx === activeIdx) {
            postVimeo(iframe, "play");
          } else {
            postVimeo(iframe, "pause");
          }
        } else {
          const video = slide.querySelector(".video-reel__video");
          if (!video) return;
          if (idx === activeIdx) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      });
    };

    /* ---- Swiper init ---- */

    const swiperOptions = {
      slidesPerView: "auto",
      centeredSlides: true,
      loop: true,
      speed: 700,
      spaceBetween: 0,
      watchSlidesProgress: true,
      autoplay:
        delayMs > 0 ? { delay: delayMs, disableOnInteraction: false } : false,
      breakpoints: {
        768: { spaceBetween: 10 },
        1025: { spaceBetween: 16 },
      },
      on: {
        afterInit(s) {
          // Second loopFix pass: loopCreate only calls it once, which leaves the
          // active slide at the last DOM position (no right-side peek). A second
          // call triggers the "append" branch, placing a slide to the right.
          s.loopFix();

          // Init every slide (originals + loop clones): muted state + listeners
          s.slides.forEach((slide) => {
            applySlideAudio(slide, true);

            const poster = slide.querySelector(".video-reel__poster");
            const iframe = getIframe(slide);
            if (iframe && poster && !iframe._vrPosterInit) {
              iframe._vrPosterInit = true;
              iframe.addEventListener(
                "load",
                () => {
                  poster.classList.add("is-hidden");
                },
                { once: true },
              );
            }
            if (poster && poster.dataset.vimeoId && !poster._vrFetchInit) {
              poster._vrFetchInit = true;
              fetchVimeoPoster(poster.dataset.vimeoId).then((url) => {
                if (url) {
                  poster.src = url;
                  poster.hidden = false;
                }
              });
            }

            const muteBtn = slide.querySelector(".video-reel__mute-btn");
            if (muteBtn && !muteBtn._vrInit) {
              muteBtn._vrInit = true;
              muteBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                toggleSlideAudio(s, slide);
              });
            }

            const container = slide.querySelector(
              ".video-reel__video-container",
            );
            if (container && !container._vrInit) {
              container._vrInit = true;
              container.addEventListener("click", (e) => {
                if (
                  e.target.closest(".video-reel__mute-btn") ||
                  e.target.closest(".video-reel__product-bar") ||
                  e.target.closest(".video-reel__product-form")
                )
                  return;
                toggleSlideAudio(s, slide);
              });
            }
          });

          playActiveVideos(s);
          setProgress(s);
        },
        slideChange(s) {
          playActiveVideos(s);
        },
        slideChangeTransitionEnd(s) {
          // Re-confirm correct video after transition (handles loop-fix snap)
          playActiveVideos(s);
          setProgress(s);
        },
        autoplayStart(s) {
          setProgress(s);
        },
        autoplayStop() {
          clearProgress();
        },
      },
    };

    if (paginationEl) {
      swiperOptions.pagination = {
        el: paginationEl,
        clickable: true,
        bulletClass: "video-reel__bullet",
        bulletActiveClass: "video-reel__bullet--active",
        renderBullet: (_, cls) =>
          `<span class="${cls}">` +
          `<svg viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">` +
          `<circle class="svg__bg-circle" cx="14" cy="14" r="11" stroke-width="2"/>` +
          `<circle class="svg__progress-circle" cx="14" cy="14" r="11" stroke-width="2"/>` +
          `<circle class="svg__dot" cx="14" cy="14" r="3"/>` +
          `</svg>` +
          `</span>`,
      };
    }

    if (prevBtn && nextBtn) {
      swiperOptions.navigation = {
        nextEl: nextBtn,
        prevEl: prevBtn,
      };
    }

    const swiper = new Swiper(swiperEl, swiperOptions);
  };

  /* ---- YouTube seamless loop (module-level, runs once) ---- */
  // YouTube iframe "ended" (state 0) sinyali gelince end-screen flash'ı
  // önlemek için hemen seekTo(0) + playVideo gönderilir.
  window.addEventListener("message", (e) => {
    try {
      const data = JSON.parse(e.data);

      // YouTube seamless loop: state 0 = ended → seekTo(0) + playVideo
      if (data.event === "onStateChange" && data.info === 0) {
        document.querySelectorAll(".video-reel__iframe").forEach((iframe) => {
          if (iframe.contentWindow !== e.source) return;
          iframe.contentWindow.postMessage(
            JSON.stringify({
              event: "command",
              func: "seekTo",
              args: [0, true],
            }),
            "*",
          );
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: "command", func: "playVideo", args: [] }),
            "*",
          );
        });
      }

      // YouTube embed error: 101/150 = video yerleştirilemez (embedding kapalı)
      if (
        data.event === "onError" &&
        (data.info === 101 || data.info === 150)
      ) {
        document.querySelectorAll(".video-reel__iframe").forEach((iframe) => {
          if (iframe.contentWindow !== e.source) return;
          iframe.style.display = "none";
          const fallback = iframe
            .closest(".video-reel__video-container")
            ?.querySelector(".video-reel__iframe-fallback");
          if (fallback) fallback.classList.add("is-visible");
        });
      }
    } catch (_) {}
  });

  /* ---- Boot ---- */

  document.querySelectorAll(".video-reel").forEach(init);

  document.addEventListener("shopify:section:load", (e) => {
    const section = e.target.querySelector(".video-reel");
    if (section) init(section);
  });
})();
